import React, { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { resetAddress } from '@r1-frontend/entities/ContactAddress/actions/resetAddress';

import { isClientSide } from '@r1-frontend/shared/helpers/ssr';

import { FullBuyContext, TFullBuyContextProperties, useAnalytics } from '~/src/features/full-buy/analytics/context';
import { ANALYTICS } from '~/src/features/full-buy/constants/analytics';
import AdditionalEquipment from '~/src/features/full-buy/containers/AdditionalEquipment';
import OneLineAddress from '~/src/features/full-buy/containers/pages/OneLineAddress';
import isFullBuyVillage from '~/src/features/full-buy/helpers/ab/isFullBuyVillage';
import isOneLineAddressScreen from '~/src/features/full-buy/helpers/ab/isOneLineAddressScreen';
import { TariffInfoPageSkeleton } from '~/src/features/full-buy/skeleton';
import selectStepName from '~/src/features/full-buy/store/selectors/selectStepName';
import { useSendReferrerToGA } from '~/src/helpers/useSendReferrerToGA';
import { TState } from '~/src/store';

import Loader from './containers/Loader';
import { Address } from './containers/pages/Address';
import { Contacts } from './containers/pages/Contacts';
import { CreateAgreementScreen } from './containers/pages/CreateAgreement';
import { EngineerRequest } from './containers/pages/EngineerRequest';
import { Equipments } from './containers/pages/Equipments';
import { EquipmentsTv } from './containers/pages/EquipmentsTv';
import { Final } from './containers/pages/Final';
import PrivateSectorEquipments from './containers/pages/PrivateSectorEquipments';
import Subscriptions from './containers/pages/Subscriptions';
import { TariffInfo } from './containers/pages/TariffInfo';
import { resetFullBuy } from './store/actions';
import { initFullBuy } from './store/actions/initFullBuy';
import { selectCurrentTariff } from './store/selectors/selectCurrentTariff';
import { getQueryParams } from './helpers';
import { ESteps } from './types';

import * as ST from './styled';

export const FullBuy = (): JSX.Element => {
    const analytics = useAnalytics();
    const router = useRouter();
    const dispatch = useDispatch();
    const { isVillage } = useContext<TFullBuyContextProperties>(FullBuyContext);

    useSendReferrerToGA(analytics.category, ANALYTICS.action.goToPage);

    const {
        salePackageId,
        tariffName: tariffNameAlias,
        monoTariff,
        tariffLine: tariffLineAlias,
        filters,
        landingAvailable,
    } = useMemo(() => getQueryParams(router.query), [router]);

    const {
        tariffPackage,
        initialized,
        stepIndex,
        stepName,
        isShownLoader,
        isAddressVerification,
    } = useSelector((state: TState) => ({
        tariffPackage: selectCurrentTariff(state),
        initialized: state.fullBuy.initialized,
        stepIndex: state.fullBuy.stepIndex,
        stepName: selectStepName(state),
        flow: state.fullBuy.flow,
        isShownLoader: state.fullBuy.isShownLoader,
        isAddressVerification: state.fullBuy._addressVerificationOnFirstStep,
    }), shallowEqual);

    const [isTimeSlotsLoading, setIsTimeSlotsLoading] = useState(false);

    const isOneLineAddressFormScreen = isOneLineAddressScreen() || isVillage;
    const isFullBuyVillageRedirect = isFullBuyVillage();

    useEffect(() => {
        // если это ЧС (те мы явно заходим в ЧС и есть редирект), то уходим. Оставлять только проверку на АБ тест некорректно, тк в нее могут попасть МКД
        if (isVillage && !isFullBuyVillageRedirect) {
            router.push('/bundles');
        }
    }, []);

    // При переключении между страницами fullBuy, прокручиваем в начало страницы
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [stepIndex]);

    // Если передали некорректный salePackageId нужно редиректить на страницу /full-buy
    // с использованием полной перезагрузкой страницы(без router.push())
    // так, как не сбрасывается состояние выбранной линейки тарифов при смене домена
    useEffect(() => {
        if (salePackageId && initialized && tariffPackage === null && isClientSide()) {
            location.href = '/full-buy';
        }
    }, [salePackageId, initialized, tariffPackage]);

    // Получение данных по линейке
    useEffect(() => {
        dispatch(initFullBuy({
            filters,
            tariffLineAlias,
            landingAvailable,

            salePackageId,
            tariffNameAlias,
            monoTariff: monoTariff === 'true',
            analytics,
        }));

        return () => {
            dispatch(resetAddress());
            dispatch(resetFullBuy());
        };
    }, []);

    // Сброс лоадера после первичной загрузки тайм-слотов
    useEffect(() => {
        if (isClientSide()) {
            const portal = document.getElementById('loader-portal');

            if (!isShownLoader && portal) {
                portal.style.minHeight = 'auto';
                portal.innerHTML === '';
                setIsTimeSlotsLoading(true);
            }
        }
    }, [isShownLoader, isTimeSlotsLoading]);

    if (!initialized) {
        return (
            <TariffInfoPageSkeleton
                title={isAddressVerification
                    ? 'Проверьте, доступен ли тариф по вашему адресу'
                    : 'Выберите тариф'
                }
            />
        );
    }

    return (
        <ST.FullBuyByPage isNotNeedGap={isShownLoader === true}>
            {stepName === ESteps.tariffInfo && <TariffInfo/>}
            {stepName === ESteps.subscriptions && <Subscriptions/>}
            {stepName === ESteps.equipments && <Equipments/>}
            {stepName === ESteps.equipmentsTv && <EquipmentsTv/>}
            {stepName === ESteps.equipmentsCottage && <PrivateSectorEquipments />}
            {stepName === ESteps.address && (
                isOneLineAddressFormScreen
                    ? <OneLineAddress/>
                    : <Address/>
            )}
            {stepName === ESteps.contacts && <Contacts/>}
            {stepName === ESteps.agreementCreated && <CreateAgreementScreen/>}
            {stepName === ESteps.timeSlots && <EngineerRequest/>}
            {stepName === ESteps.final && <Final/>}
            {(stepName === ESteps.agreementCreated || stepName === ESteps.timeSlots && !isTimeSlotsLoading) &&
                <Loader/>
            }
            <AdditionalEquipment/>
            <div id='loader-portal'></div>
        </ST.FullBuyByPage>
    );
};
